import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {select, Store} from '@ngrx/store';
import {catchError, map, mergeMap, withLatestFrom} from 'rxjs/operators';
import * as CharacterActions from './character.actions';
import {loadCharacterRollsById} from './character.actions';
import * as CampaignSelectors from '../campaigns/campaign.selectors';
import {CharactersService} from 'gamesis-story-client';
import {forkJoin, of} from 'rxjs';
import {setCampaign} from '../campaigns/campaign.actions';
import {selectCurrentCharacter} from './character.selectors';

@Injectable()
export class CharacterEffects {
    loadCharacters$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CharacterActions.loadCharacters,
                setCampaign),
            withLatestFrom(this.store.select(CampaignSelectors.selectCurrentCampaignId)),
            mergeMap(([action, campaign]) => {
                if (campaign) {
                    return forkJoin([
                        this.service.getCharacters(campaign),
                        this.service.me(campaign)
                    ]).pipe(
                        map(([characters, myCharacters]) => CharacterActions.loadCharactersSuccess({characters, myCharacters})),
                        catchError(error => of(CharacterActions.charactersFailure({error})))
                    );
                } else {
                    return of(CharacterActions.charactersFailure({error: 'No campaign selected'}));
                }
            })
        )
    );

    loadCharacter$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CharacterActions.loadCharacter),
            withLatestFrom(this.store.select(CampaignSelectors.selectCurrentCampaignId)),
            mergeMap(([action, selectedCampaignId]) => {
                if (selectedCampaignId) {
                    return this.service.getCharacter(selectedCampaignId, action.id)
                        .pipe(
                            map(character => CharacterActions.loadCharacterSuccess({character})),
                            catchError(error => of(CharacterActions.charactersFailure({error})))
                        );
                } else {
                    return of(CharacterActions.charactersFailure({error: 'No campaign selected'}));
                }
            })
        )
    );

    loadCharacterRollsById$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CharacterActions.loadCharacterRollsById),
            // Combine the action with the latest campaign ID
            withLatestFrom(this.store.select(CampaignSelectors.selectCurrentCampaignId)),
            // Perform the selection of rolls within mergeMap
            mergeMap(([action, campaign]) => {
                if (!campaign) {
                    return of(CharacterActions.charactersFailure({error: 'No campaign selected'}));
                }
                return this.service.getRolls(campaign, action.id).pipe(
                    map(rolls => CharacterActions.loadCharacterRollsSuccess({id: action.id, rolls})),
                    catchError(error => of(CharacterActions.charactersFailure({error})))
                );
            })
        )
    );

    loadCharacterRolls$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CharacterActions.loadCharacterRolls),
            withLatestFrom(
                this.store.pipe(select(selectCurrentCharacter)),
            ),
            mergeMap(([_, character]) => {
                if (!character) {
                    return of(CharacterActions.charactersFailure({error: 'No character selected'}));
                }
                return of(loadCharacterRollsById({id: character.id}));
            })
        )
    );

    addCharacter$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CharacterActions.addCharacter),
            withLatestFrom(this.store.pipe(select(CampaignSelectors.selectCurrentCampaignId))),
            mergeMap(([action, selectedCampaignId]) => this.service.createCharacter(selectedCampaignId, action.character)
                .pipe(
                    map(character => CharacterActions.addCharacterSuccess({character: character})),
                    catchError(error => of(CharacterActions.charactersFailure({error})))
                ))
        )
    );

    updateCharacter$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CharacterActions.updateCharacter),
            withLatestFrom(this.store.pipe(select(CampaignSelectors.selectCurrentCampaignId))),
            mergeMap(([action, campaign]) => this.service.updateCharacter(campaign, action.id, action.character)
                .pipe(
                    map(() => CharacterActions.loadCharacter({id: action.id})),
                    catchError(error => of(CharacterActions.charactersFailure({error})))
                ))
        )
    );

    activateCharacter$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CharacterActions.activateCharacter),
            withLatestFrom(this.store.pipe(select(CampaignSelectors.selectCurrentCampaignId))),
            mergeMap(([action, campaign]) => {
                return this.service.updateCharacter(campaign, action.id, {
                    ...action.character,
                    inactive: false
                }).pipe(
                    map(() => CharacterActions.activateCharacterSuccess({id: action.id})),
                    catchError(error => of(CharacterActions.charactersFailure({error})))
                );
            })
        )
    );

    deactivateCharacter$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CharacterActions.deactivateCharacter),
            withLatestFrom(this.store.pipe(select(CampaignSelectors.selectCurrentCampaignId))),
            mergeMap(([action, campaign]) => {
                return this.service.updateCharacter(campaign, action.id, {
                    ...action.character,
                    inactive: true
                }).pipe(
                    map(() => CharacterActions.deactivateCharacterSuccess({id: action.id})),
                    catchError(error => of(CharacterActions.charactersFailure({error})))
                );
            })
        )
    );

    deleteCharacter$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CharacterActions.deleteCharacter),
            withLatestFrom(this.store.select(CampaignSelectors.selectCurrentCampaignId)),
            mergeMap(([action, campaign]) => this.service.deleteCharacter(campaign, action.id)
                .pipe(
                    map(() => CharacterActions.deleteCharacterSuccess({id: action.id})),
                    catchError(error => of(CharacterActions.charactersFailure({error})))
                ))
        )
    );

    setAvatar$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CharacterActions.setAvatar),
            withLatestFrom(this.store.select(CampaignSelectors.selectCurrentCampaignId)),
            mergeMap(([action, campaign]) => this.service.avatar(campaign, action.id, action.file)
                .pipe(
                    map(() => CharacterActions.loadCharacter({id: action.id})),
                    catchError(error => of(CharacterActions.charactersFailure({error})))
                )
            )
        )
    );

    removeAvatar$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CharacterActions.removeAvatar),
            withLatestFrom(this.store.select(CampaignSelectors.selectCurrentCampaignId)),
            mergeMap(([action, campaign]) => this.service.removeAvatar(campaign, action.id)
                .pipe(
                    map(() => CharacterActions.loadCharacter({id: action.id})),
                    catchError(error => of(CharacterActions.charactersFailure({error})))
                )
            )
        )
    );

    deleteRoll$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CharacterActions.deleteRoll),
            withLatestFrom(this.store.select(CampaignSelectors.selectCurrentCampaignId)),
            mergeMap(([action, campaign]) => this.service.deleteRoll(campaign, action.character, action.id)
                .pipe(
                    map(() => CharacterActions.deleteRollSuccess({id: action.character})),
                    catchError(error => of(CharacterActions.charactersFailure({error})))
                ))
        )
    );


    constructor(
        private actions$: Actions,
        private store: Store,
        private service: CharactersService
    ) {
    }
}
